import dayjs from 'dayjs';
import type { DataTableColumn } from 'mantine-datatable';

import { BooleanCell } from '#core/components/dashboard/panels/table/cells/BooleanCell.tsx';
import { DateCell } from '#core/components/dashboard/panels/table/cells/DateCell.tsx';
import { LinkedAssetCell } from '#core/components/dashboard/panels/table/cells/LinkedAssetCell.tsx';
import { NumberCell } from '#core/components/dashboard/panels/table/cells/NumberCell.tsx';
import { isInstanceReference } from '#core/lib/cognite/types.ts';
import { humanizeKey } from '#core/lib/formatters/humanize-key.ts';
import { inferNumberFormat } from '#core/lib/formatters/number-decimals.ts';
import { ISO_DATE_REGEX_STRICT } from '#core/lib/utils/date.ts';

// Detected column value types (includes linkedAsset which is specific to table display)
type ColumnType = 'boolean' | 'date' | 'linkedAsset' | 'number' | 'string';

/**
 * Checks if a value is a date-like value (Date object or ISO 8601 string).
 */
function isDateValue(value: unknown): boolean {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return true;
  }

  if (typeof value === 'string' && ISO_DATE_REGEX_STRICT.test(value)) {
    const parsed = dayjs(value);
    return parsed.isValid();
  }

  return false;
}

/**
 * Detects the type of values in a column by sampling the first non-null values.
 *
 * Detection priority:
 * 1. Boolean: all sampled values are true/false
 * 2. LinkedAsset: all sampled values are objects with space and externalId
 * 3. Date: all sampled values are Date objects or ISO 8601 strings
 * 4. Number: all sampled values are numbers (not NaN)
 * 5. String: fallback for everything else
 */
export function detectColumnType(values: unknown[]): ColumnType {
  // Sample first 10 non-null values
  const sampled = values.filter((v) => v != null).slice(0, 10);

  if (sampled.length === 0) {
    return 'string';
  }

  // Check boolean - all values must be true or false
  if (sampled.every((v) => typeof v === 'boolean')) {
    return 'boolean';
  }

  // Check binary numeric — must check ALL values (not just sampled) to avoid
  // false positives on numeric columns that happen to start with 0/1.
  // Common in timeseries like onlineStatus where every value is exactly 0 or 1.
  const allNonNull = values.filter((v) => v != null);
  if (allNonNull.length > 0 && allNonNull.every((v) => v === 0 || v === 1)) {
    return 'boolean';
  }

  // Check linked asset - objects with space and externalId
  if (sampled.every((v) => isInstanceReference(v))) {
    return 'linkedAsset';
  }

  // Check date - Date objects or parseable ISO 8601 strings
  if (sampled.every((v) => isDateValue(v))) {
    return 'date';
  }

  // Check number - all values must be numbers (not NaN)
  if (sampled.every((v) => typeof v === 'number' && !Number.isNaN(v))) {
    return 'number';
  }

  return 'string';
}

export interface ColumnDefOptions {
  onLinkedAssetClick?: (ref: { space: string; externalId: string }) => void;
  decimalScale?: number;
}

/**
 * Creates a mantine-datatable column definition with appropriate formatting
 * based on detected data type.
 *
 * @param accessor - The column accessor (property name)
 * @param values - All values for this column to detect type from
 * @param options - Optional configuration for column behavior
 * @returns Column definition with render function based on detected type
 */
export function createColumnDef(accessor: string, values: unknown[], options?: ColumnDefOptions): DataTableColumn {
  const columnType = detectColumnType(values);

  // Default title: humanized accessor (camelCase / snake_case → Title Case).
  // Callers (e.g. buildColumnsFromConfig) override via `colConfig.title ?? baseDef.title`.
  const baseColumn: DataTableColumn = {
    accessor,
    title: humanizeKey(accessor),
  };

  // biome-ignore lint/nursery/noUnnecessaryConditions: switch is necessary for type-based pattern matching
  switch (columnType) {
    case 'boolean':
      return {
        ...baseColumn,
        textAlign: 'center',
        render: (record) => {
          const value = record[accessor];
          if (typeof value === 'boolean') {
            return <BooleanCell value={value} />;
          }
          // Binary numeric (0/1) from timeseries like onlineStatus
          if (value === 0 || value === 1) {
            return <BooleanCell value={value === 1} />;
          }
          return String(value ?? '');
        },
      };

    case 'linkedAsset':
      return {
        ...baseColumn,
        render: (record) => {
          const value = record[accessor];
          if (isInstanceReference(value)) {
            return <LinkedAssetCell onClick={options?.onLinkedAssetClick} value={value} />;
          }
          // Fallback for unexpected value types
          return value == null ? '' : String(value);
        },
      };

    case 'date':
      return {
        ...baseColumn,
        render: (record) => {
          const value = record[accessor];
          if (value != null) {
            return <DateCell value={value} />;
          }
          return '';
        },
      };

    case 'number': {
      const numericValues = values.filter((v): v is number => typeof v === 'number');
      const inferred = inferNumberFormat(numericValues, accessor);
      const scale = options?.decimalScale ?? inferred.scale;
      const useThousandSeparator = inferred.useThousandSeparator;
      return {
        ...baseColumn,
        textAlign: 'right',
        render: (record) => {
          const value = record[accessor];
          if (typeof value === 'number') {
            return <NumberCell decimalScale={scale} thousandSeparator={useThousandSeparator} value={value} />;
          }
          return String(value ?? '');
        },
      };
    }

    default:
      // String type - safely render any value including objects
      return {
        ...baseColumn,
        render: (record) => {
          const value = record[accessor];
          if (value === null || value === undefined) {
            return '';
          }
          if (typeof value === 'object') {
            // Stringify objects for display
            return JSON.stringify(value);
          }
          return String(value);
        },
      };
  }
}
